import {z} from 'zod'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n'
import {useCallback, useMemo} from 'react'
import {accessTypeKeyNameToDefaultDuration} from '@/features/agenda/utils'
import {EventTypeKeyName} from '@/features/agenda/types'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {InputSelectPlannedAccess} from '@/features/planned-accesses/components/input-select-planned-access/InputSelectPlannedAccess'
import {ObjectKeys} from '@/utilities/helpers'
import {disabledAccessTypeByProjectType} from '@/features/access-types/utils'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'

const AgendaEventAccessTypeSelectorSchema = AgendaEventFieldsSchema.pick({
    consultants: true,
    patients: true,
    duration: true,
    category: true,
    isRepeated: true,
    repetitionData: true
})
type AgendaEventAccessTypeSelectorSchema = z.infer<typeof AgendaEventAccessTypeSelectorSchema>

export const AgendaEventAccessTypeSelector = ({index}: {index: number}) => {
    const {t} = useTranslation()
    const {control, setValue, clearErrors, resetField} = useFormContext<AgendaEventAccessTypeSelectorSchema>()
    const patients = useWatch({control, name: `patients`})
    const category = useWatch({control, name: `category`})
    const currentDuration = useWatch({control, name: `duration`})
    const consultant = useWatch({control, name: `consultants.${index}`})

    const {data: specializations} = useGetUserSpecializations(
        {
            search: consultant?.specializationName,
            limit: 1
        },
        {
            enabled: !!consultant?.specializationName && !consultant?.specializationId
        }
    )

    const selectedSpecializationId = useMemo(() => {
        if (!specializations?.pages?.[0]?.data?.length) return undefined
        return specializations.pages[0].data.find(
            specialization => specialization.name === consultant?.specializationName
        )
    }, [specializations])

    const {
        field: {onChange, ...field},
        fieldState
    } = useController({control, name: `consultants.${index}.accessType`})

    const {isSuccess: hasPlannedAccesses} = useGetPlannedAccessesDetails({
        urlParams: {projectId: patients?.[0]?.project?.id ?? ''},
        options: {enabled: !!patients?.[0]?.project?.id}
    })

    const onSelectAccessTypeSideEffect = useCallback(
        (value: EventTypeKeyName) => {
            const duration = accessTypeKeyNameToDefaultDuration[value]
            setValue('duration', duration.toString(), {shouldDirty: true})
            resetField('repetitionData')
            setValue('isRepeated', false)
            clearErrors(`duration`)
        },
        [setValue, clearErrors, currentDuration]
    )

    const project = useMemo(() => {
        if (!patients?.length) return undefined
        return patients[0]?.project
    }, [patients])

    const targetProject = useMemo(() => {
        if (!project || !hasPlannedAccesses || patients?.length > 1) return undefined
        return project
    }, [project, hasPlannedAccesses, patients])

    const disabledAccessTypes = useMemo(() => {
        // disable all cyclic access types if the project type is not defined
        if (!project?.type) {
            return disabledAccessTypeByProjectType['continuous']
        }

        // disable all special access types if the project type is different from the patient project type
        if (!patients?.every(patient => patient.project?.type === project?.type)) {
            return ObjectKeys(disabledAccessTypeByProjectType).flatMap(
                keyName => disabledAccessTypeByProjectType[keyName]
            )
        }

        // disable all specific access types based on the provided project type
        return disabledAccessTypeByProjectType[project.type]
    }, [project, patients])

    return (
        <InputSelectPlannedAccess
            {...field}
            label={t('commons:access_type')}
            placeholder={t('agenda:select_access_type_placeholder')}
            projectId={targetProject?.id}
            disabled={!consultant?.id || (!patients?.[0]?.id && !consultant?.specializationId)}
            errorMessage={fieldState.error?.message}
            category={category || undefined}
            specializationId={consultant?.specializationId || selectedSpecializationId?.id}
            disabledAccessTypeKeyNames={disabledAccessTypes}
            onChange={value => {
                onChange(value)
                if (value?.accessType) {
                    onSelectAccessTypeSideEffect(value?.accessType)
                }
            }}
        />
    )
}
